import { useState } from "react";
import { CodeEditor } from "../components/CodeEditor.tsx";
import { EnvEditor } from "../components/EnvEditor.tsx";
import { PannelloDiviso } from "../components/PannelloDiviso.tsx";
import { Badge, Button, Card, Empty, ErrorNotice, Field, Input, Spinner } from "../components/ui.tsx";
import type { Messaggio } from "../../i18n/index.ts";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.tsx";
import { useAction, useQuery } from "../lib/hooks.ts";
import { linkProps, navigate } from "../lib/router.ts";
import type { Session, Stack } from "../lib/types.ts";

/**
 * Importazione di stack già presenti sull'host.
 *
 * Serve a chi arriva a Portman con stack avviati altrove: la configurazione
 * viene ricostruita dai container invece di essere riscritta a memoria.
 *
 * L'anteprima precede l'importazione di proposito. Una configurazione
 * ricostruita non è identica all'originale — commenti e ordine di avvio Docker
 * non li conserva — e va guardata prima di adottarla.
 */

interface Progetto {
  readonly name: string;
  readonly services: readonly string[];
  readonly running: number;
  readonly total: number;
  readonly alreadyManaged: boolean;
}

interface Anteprima {
  readonly composeYaml: string;
  readonly env: Record<string, string>;
  readonly note: readonly Messaggio[];
}

export function ImportPage({ sessione }: { sessione: Session }) {
  const progetti = useQuery<{ projects: Progetto[] }>("/importazione");
  const [scelto, setScelto] = useState<string | null>(null);

  const puòImportare = sessione.permissions.includes("stack:write");
  const t = useT();

  return (
    <div className="space-y-5">
      <div>
        <a {...linkProps("/stacks")} className="text-sm tenue hover:underline">
          ← {t.t("nav.stack")}
        </a>
        <h1 className="mt-1 text-xl font-semibold">{t.t("import.titolo")}</h1>
        <p className="text-sm tenue">{t.t("import.intro")}</p>
      </div>

      <ErrorNotice error={progetti.error} />
      {progetti.loading && <Spinner className="tenue" />}

      {progetti.data?.projects.length === 0 && (
        <Empty
          title={t.t("import.vuoto")}
          hint={t.t("import.vuotoAiuto")}
        />
      )}

      {progetti.data && progetti.data.projects.length > 0 && (
        <Card className="divide-y overflow-hidden">
          {progetti.data.projects.map((progetto) => (
            <div key={progetto.name} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <span className="font-medium">{progetto.name}</span>

              <Badge tone={progetto.running > 0 ? "success" : "neutral"}>
                {t.t("import.attivi", { attivi: progetto.running, totale: progetto.total })}
              </Badge>

              {progetto.alreadyManaged && <Badge tone="accent">{t.t("import.giaGestito")}</Badge>}

              <span className="min-w-0 flex-1 truncate text-xs tenue">
                {progetto.services.join(", ")}
              </span>

              <Button
                className="text-xs"
                disabled={progetto.alreadyManaged}
                onClick={() => setScelto(scelto === progetto.name ? null : progetto.name)}
              >
                {t.t(scelto === progetto.name ? "comune.chiudi" : "import.esamina")}
              </Button>
            </div>
          ))}
        </Card>
      )}

      {scelto && (
        <AnteprimaProgetto
          key={scelto}
          progetto={scelto}
          puòImportare={puòImportare}
          onImportato={() => progetti.reload()}
        />
      )}
    </div>
  );
}

function AnteprimaProgetto({
  progetto,
  puòImportare,
  onImportato,
}: {
  progetto: string;
  puòImportare: boolean;
  onImportato: () => void;
}) {
  const t = useT();
  const anteprima = useQuery<Anteprima>(`/importazione/${encodeURIComponent(progetto)}`);
  const [nome, setNome] = useState(progetto);

  const importazione = useAction(async () => {
    const { stack } = await api.post<{ stack: Stack }>("/importazione", {
      project: progetto,
      ...(nome !== progetto ? { name: nome } : {}),
    });

    onImportato();
    navigate(`/stacks/${stack.id}`);
  });

  if (anteprima.loading) return <Spinner className="tenue" />;
  if (anteprima.error) return <ErrorNotice error={anteprima.error} />;
  if (!anteprima.data) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold">{t.t("import.ricostruita", { progetto })}</h2>

      {anteprima.data.note.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/6 px-4 py-3">
          <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
            {t.t("import.note")}
          </h3>
          <ul className="mt-1.5 space-y-1.5 text-sm text-amber-700 dark:text-amber-400">
            {anteprima.data.note.map((nota, indice) => (
              <li key={indice}>{t.traduci(nota) ?? nota.chiave}</li>
            ))}
          </ul>
        </div>
      )}

      <PannelloDiviso
        memoria="importazione-anteprima"
        sinistra={
          <div className="group space-y-1.5 pr-1">
            <span className="text-sm font-medium">{t.t("import.composeRicostruito")}</span>
            <CodeEditor
              value={anteprima.data.composeYaml}
              linguaggio="yaml"
              readOnly
              righe={26}
              espandibile
              ricercabile
              titolo={t.t("import.titoloEditor", { progetto })}
            />
          </div>
        }
        destra={
          <div className="space-y-1.5 pl-1">
            <EnvEditor value={anteprima.data.env} onChange={() => {}} readOnly />
            <span className="block text-xs tenue">{t.t("import.sensibili")}</span>
          </div>
        }
      />

      <ErrorNotice error={importazione.error} />

      {puòImportare && (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t.t("import.nome")}>
            <Input value={nome} onChange={(evento) => setNome(evento.target.value)} className="w-64" />
          </Field>

          <Button
            variant="primary"
            loading={importazione.running}
            disabled={!nome}
            onClick={() => void importazione.run()}
          >
            {t.t("import.importa")}
          </Button>

          <span className="text-sm tenue">{t.t("import.dopo")}</span>
        </div>
      )}
    </div>
  );
}
